/**
 * ops2026 — Verified crawlers (ADR-0245 §4.4.2).
 *
 * The verified-crawler list (Googlebot, Bingbot, OpenAI etc. IP
 * ranges) is the hard-invariant exemption surface for Sill's firewall
 * — a block entry whose target overlaps a verified crawler is rejected
 * at API write time. Every change to the list goes through this
 * review: the daily refresh (or the "Fetch now" button) stages into
 * `verified_crawler_range_pending`; a human promotes a pending
 * snapshot to active here.
 *
 * The server enforces the role floors (viewer+ reads, admin+ fetches,
 * owner approves/rejects); this view greys out buttons the signed-in
 * staff can't press and never pretends an action is available.
 */

import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  AdminVerifiedCrawlerActiveSnapshot,
  AdminVerifiedCrawlerLastRefresh,
  AdminVerifiedCrawlerOperatorRefreshOutcome,
  AdminVerifiedCrawlerPendingSnapshot,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { hasRole } from '../../lib/roles.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

type Phase = 'loading' | 'ready' | 'error';

export function VerifiedCrawlersView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const role = auth.state.kind === 'authenticated' ? auth.state.staff.role : undefined;

  const canFetch = hasRole(role, 'admin');
  const canApprove = hasRole(role, 'owner');

  const [phase, setPhase] = useState<Phase>('loading');
  const [active, setActive] = useState<AdminVerifiedCrawlerActiveSnapshot | null>(null);
  const [pending, setPending] = useState<readonly AdminVerifiedCrawlerPendingSnapshot[]>([]);
  const [refreshEnabled, setRefreshEnabled] = useState<boolean>(false);
  const [lastRefresh, setLastRefresh] = useState<AdminVerifiedCrawlerLastRefresh | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setPhase('loading');
    setError(null);
    try {
      const res = await api.getVerifiedCrawlerReview();
      setActive(res.active);
      setPending(res.pending);
      setRefreshEnabled(res.refresh_enabled);
      setLastRefresh(res.last_refresh);
      setPhase('ready');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load snapshots.');
      setPhase('error');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const onFetchNow = useCallback(async (): Promise<void> => {
    setBusy('fetch');
    setNotice(null);
    try {
      const res = await api.fetchVerifiedCrawlers();
      if (res.outcome === 'no_change') {
        setNotice({ tone: 'ok', text: 'Fetched — no change against the active snapshot.' });
      } else {
        setNotice({
          tone: 'warn',
          text:
            `Staged a new pending snapshot (${res.alarmed_operators.length} operator`
            + `${res.alarmed_operators.length === 1 ? '' : 's'} alarmed: `
            + `${res.alarm_reasons.join(', ')}).`,
        });
      }
      await load();
    } catch (err) {
      setNotice({
        tone: 'bad',
        text: err instanceof ApiError ? err.message : 'Fetch failed.',
      });
    } finally {
      setBusy(null);
    }
  }, [api, load]);

  const onApprove = useCallback(
    async (
      snapshot: AdminVerifiedCrawlerPendingSnapshot,
      confirmEmpty: boolean,
    ): Promise<void> => {
      setBusy(`approve:${snapshot.snapshot_id}`);
      setNotice(null);
      try {
        const res = await api.approveVerifiedCrawlerSnapshot(snapshot.snapshot_id, {
          snapshot_hash: snapshot.snapshot_hash,
          ...(confirmEmpty ? { confirm_empty_operators: true } : {}),
        });
        setNotice({
          tone: 'ok',
          text: `Promoted ${snapshot.snapshot_id} (${res.rows_written} rows written).`,
        });
        await load();
      } catch (err) {
        setNotice({
          tone: 'bad',
          text: err instanceof ApiError ? err.message : 'Approve failed.',
        });
      } finally {
        setBusy(null);
      }
    },
    [api, load],
  );

  const onReject = useCallback(
    async (snapshot: AdminVerifiedCrawlerPendingSnapshot, reason: string): Promise<void> => {
      setBusy(`reject:${snapshot.snapshot_id}`);
      setNotice(null);
      try {
        await api.rejectVerifiedCrawlerSnapshot(snapshot.snapshot_id, {
          snapshot_hash: snapshot.snapshot_hash,
          reason,
        });
        setNotice({ tone: 'ok', text: `Rejected ${snapshot.snapshot_id}.` });
        await load();
      } catch (err) {
        setNotice({
          tone: 'bad',
          text: err instanceof ApiError ? err.message : 'Reject failed.',
        });
      } finally {
        setBusy(null);
      }
    },
    [api, load],
  );

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted }}>
        OPS · VERIFIED CRAWLERS
      </div>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 32,
          letterSpacing: -1,
          color: C.text,
          margin: '4px 0 4px',
        }}
      >
        Verified-crawler list
      </h1>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          maxWidth: 720,
          lineHeight: 1.5,
          margin: '0 0 20px',
        }}
      >
        The IP ranges that identify Googlebot, Bingbot, OpenAI, Anthropic
        and friends. A merchant block that overlaps a verified crawler
        is rejected at write time, and the Worker rechecks at request
        time. The daily refresh job stages every delta here — Approve
        promotes the proposal to the live firewall exemption; Reject
        marks it discarded.
      </p>

      {notice !== null ? (
        <div
          style={{
            padding: 10,
            marginBottom: 14,
            borderRadius: 2,
            border: `1px solid ${
              notice.tone === 'ok' ? C.limeFg : notice.tone === 'warn' ? C.amberFg : C.coralFg
            }`,
            background:
              notice.tone === 'ok' ? C.limeDim : notice.tone === 'warn' ? C.amberDim : C.coralDim,
            color: C.text,
            fontFamily: sansFont,
            fontSize: 13,
          }}
        >
          {notice.text}
        </div>
      ) : null}

      {phase === 'loading' ? (
        <Card>
          <div
            style={{
              padding: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
            }}
          >
            <RefreshCw size={14} style={{ animation: 'spin 1.4s linear infinite' }} />
            Loading…
          </div>
        </Card>
      ) : null}

      {phase === 'error' ? (
        <Card>
          <div style={{ padding: 16, color: C.coralFg, fontFamily: sansFont, fontSize: 13 }}>
            {error ?? 'Unknown error.'}
          </div>
        </Card>
      ) : null}

      {phase === 'ready' && active !== null ? (
        <>
          <ActiveSnapshotCard
            active={active}
            refreshEnabled={refreshEnabled}
            lastRefresh={lastRefresh}
            canFetch={canFetch}
            busyFetch={busy === 'fetch'}
            onFetchNow={onFetchNow}
            onReload={load}
          />

          <div style={{ height: 20 }} />

          {pending.length === 0 ? (
            <Card>
              <div
                style={{
                  padding: 16,
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.muted,
                }}
              >
                No pending snapshots awaiting review.
              </div>
            </Card>
          ) : (
            pending.map((p) => (
              <PendingSnapshotCard
                key={p.snapshot_id}
                active={active}
                snapshot={p}
                canApprove={canApprove}
                busy={busy}
                onApprove={onApprove}
                onReject={onReject}
              />
            ))
          )}
        </>
      ) : null}
    </div>
  );
}

function ActiveSnapshotCard({
  active,
  refreshEnabled,
  lastRefresh,
  canFetch,
  busyFetch,
  onFetchNow,
  onReload,
}: {
  active: AdminVerifiedCrawlerActiveSnapshot;
  refreshEnabled: boolean;
  lastRefresh: AdminVerifiedCrawlerLastRefresh | null;
  canFetch: boolean;
  busyFetch: boolean;
  onFetchNow: () => Promise<void>;
  onReload: () => Promise<void>;
}): JSX.Element {
  const C = useC();
  const totalV4 = active.operators.reduce((a, o) => a + o.ipv4_count, 0);
  const totalV6 = active.operators.reduce((a, o) => a + o.ipv6_count, 0);
  return (
    <Card>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 22,
              color: C.text,
            }}
          >
            Active snapshot
          </div>
          <Pill tone={active.source === 'bundled' ? 'amber' : 'mint'}>
            {active.source === 'bundled' ? 'bundled seed' : 'db'}
          </Pill>
          {totalV4 + totalV6 === 0 ? (
            <Pill tone="coral">
              <AlertTriangle size={10} /> empty — enforcement publishes zero blocks
            </Pill>
          ) : (
            <Pill tone="lime">
              <ShieldCheck size={10} /> {totalV4 + totalV6} ranges live
            </Pill>
          )}
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
            {active.snapshot_id} · catalog v{active.catalog_version}
          </span>
        </div>

        <div
          style={{
            marginTop: 6,
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            wordBreak: 'break-all',
          }}
        >
          sha256 {active.snapshot_hash}
          {active.committed_at !== null ? ` · committed ${active.committed_at}` : ''}
        </div>

        <div
          style={{
            marginTop: 14,
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, 1.5fr) 1fr 60px 60px',
            gap: 6,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          <div style={{ letterSpacing: 1.2 }}>OPERATOR</div>
          <div style={{ letterSpacing: 1.2 }}>SOURCE</div>
          <div style={{ letterSpacing: 1.2, textAlign: 'right' }}>V4</div>
          <div style={{ letterSpacing: 1.2, textAlign: 'right' }}>V6</div>
          {active.operators.map((op) => (
            <OperatorRow key={op.operator} op={op} />
          ))}
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => void onFetchNow()}
            disabled={!canFetch || busyFetch}
            style={{
              background: !canFetch ? C.surface2 : C.text,
              color: !canFetch ? C.muted : C.surface,
              border: 'none',
              padding: '6px 12px',
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 2,
              cursor: canFetch && !busyFetch ? 'pointer' : 'default',
              opacity: busyFetch ? 0.6 : 1,
            }}
            title={canFetch ? 'Run one refresh tick now' : 'Admin only'}
          >
            {busyFetch ? 'Fetching…' : 'Fetch now'}
          </button>
          <button
            type="button"
            onClick={() => void onReload()}
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              padding: '6px 12px',
              fontFamily: sansFont,
              fontSize: 12,
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
            {refreshEnabled
              ? 'Scheduled refresh is on (default; set VERIFIED_CRAWLER_REFRESH_ENABLED=false to disable).'
              : 'Scheduled refresh is OFF (VERIFIED_CRAWLER_REFRESH_ENABLED=false). Fetch now is manual-only.'}
          </span>
        </div>

        {lastRefresh !== null ? (
          <LastRefreshBlock lastRefresh={lastRefresh} />
        ) : null}
      </div>
    </Card>
  );
}

function LastRefreshBlock({
  lastRefresh,
}: {
  lastRefresh: AdminVerifiedCrawlerLastRefresh;
}): JSX.Element {
  const C = useC();
  const hasRun = lastRefresh.completed_at !== null;
  return (
    <div
      style={{
        marginTop: 16,
        padding: 12,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.2,
          color: C.muted,
          marginBottom: 6,
        }}
      >
        LAST AUTOMATIC REFRESH
      </div>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
        {hasRun
          ? `${lastRefresh.completed_at} · ${lastRefresh.outcome ?? 'unknown'}`
          : 'Not run yet on this install.'}
      </div>
      {hasRun ? (
        <div
          style={{
            marginTop: 10,
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, 1fr) 90px auto',
            gap: 4,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          {lastRefresh.operators.map((op) => (
            <OperatorOutcomeRow key={op.operator} op={op} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function OperatorOutcomeRow({
  op,
}: {
  op: AdminVerifiedCrawlerOperatorRefreshOutcome;
}): JSX.Element {
  const C = useC();
  const tone =
    op.outcome === 'ok' ? 'lime'
    : op.outcome === 'pending' ? 'amber'
    : op.outcome === 'kept_previous' ? 'coral'
    : 'sky';
  const label =
    op.outcome === 'ok' ? 'ok'
    : op.outcome === 'pending' ? `pending: ${op.reason ?? ''}`
    : op.outcome === 'kept_previous' ? `kept previous: ${op.reason ?? ''}`
    : 'unknown';
  const detail = op.active_count !== null && op.proposed_count !== null
    ? `was ${op.active_count} / proposed ${op.proposed_count}`
    : '';
  return (
    <>
      <div style={{ color: C.text, fontFamily: sansFont, fontSize: 12 }}>
        {op.operator}
      </div>
      <div>
        <Pill tone={tone}>{label}</Pill>
      </div>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
        {detail}
      </div>
    </>
  );
}

function OperatorRow({
  op,
}: {
  op: AdminVerifiedCrawlerActiveSnapshot['operators'][number];
}): JSX.Element {
  const C = useC();
  const zero = op.ipv4_count + op.ipv6_count === 0;
  return (
    <>
      <div style={{ color: C.text, fontFamily: sansFont, fontSize: 12 }}>
        {op.organization}{' '}
        <span style={{ color: C.muted, fontFamily: monoFont, fontSize: 10 }}>
          {op.operator}
        </span>
      </div>
      <div
        style={{
          color: C.muted,
          fontFamily: monoFont,
          fontSize: 10,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {op.source_url.replace(/^https?:\/\//, '')}
      </div>
      <div
        style={{
          textAlign: 'right',
          color: zero ? C.coralFg : C.text,
          fontFamily: monoFont,
          fontSize: 11,
        }}
      >
        {op.ipv4_count}
      </div>
      <div
        style={{
          textAlign: 'right',
          color: zero ? C.coralFg : C.text,
          fontFamily: monoFont,
          fontSize: 11,
        }}
      >
        {op.ipv6_count}
      </div>
    </>
  );
}

function PendingSnapshotCard({
  active,
  snapshot,
  canApprove,
  busy,
  onApprove,
  onReject,
}: {
  active: AdminVerifiedCrawlerActiveSnapshot;
  snapshot: AdminVerifiedCrawlerPendingSnapshot;
  canApprove: boolean;
  busy: string | null;
  onApprove: (snapshot: AdminVerifiedCrawlerPendingSnapshot, confirmEmpty: boolean) => Promise<void>;
  onReject: (snapshot: AdminVerifiedCrawlerPendingSnapshot, reason: string) => Promise<void>;
}): JSX.Element {
  const C = useC();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  const busyApprove = busy === `approve:${snapshot.snapshot_id}`;
  const busyReject = busy === `reject:${snapshot.snapshot_id}`;

  // The server already decides what to refuse; the UI greys the
  // Approve button when any of those server-side blockers would fire
  // AND the operator has not opted in to the override.
  const validation = useValidationBlockers(active, snapshot);
  const needsEmptyConfirm = snapshot.operators_emptied.length > 0;
  const approveBlocked =
    !canApprove
    || busyApprove
    || !validation.cidrs_valid
    || !validation.source_hosts_valid
    || validation.is_empty
    || (needsEmptyConfirm && !confirmEmpty);

  return (
    <Card>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: serifFont, fontSize: 20, color: C.text }}>
            Pending snapshot
          </div>
          {snapshot.alarm_reasons.map((reason) => (
            <Pill
              key={reason}
              tone={alarmTone(reason)}
            >
              <ShieldAlert size={10} /> {reason}
            </Pill>
          ))}
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
            {snapshot.snapshot_id} · observed {snapshot.observed_at}
          </span>
        </div>

        <div
          style={{
            marginTop: 4,
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            wordBreak: 'break-all',
          }}
        >
          sha256 {snapshot.snapshot_hash}
        </div>

        <div
          style={{
            marginTop: 14,
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, 1.5fr) 70px 70px 70px 70px 1fr',
            gap: 6,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          <div style={{ letterSpacing: 1.2 }}>OPERATOR</div>
          <div style={{ letterSpacing: 1.2, textAlign: 'right' }}>BEFORE</div>
          <div style={{ letterSpacing: 1.2, textAlign: 'right' }}>AFTER</div>
          <div style={{ letterSpacing: 1.2, textAlign: 'right' }}>+ADD</div>
          <div style={{ letterSpacing: 1.2, textAlign: 'right' }}>−REM</div>
          <div style={{ letterSpacing: 1.2 }}>ALARM</div>
          {snapshot.operators.map((op) => (
            <PendingOperatorRow key={op.operator} op={op} />
          ))}
        </div>

        {!validation.cidrs_valid ? (
          <BlockerLine
            icon={<XCircle size={12} />}
            tone="coral"
            text="One or more CIDRs are too broad or malformed. Approve is blocked."
          />
        ) : null}
        {!validation.source_hosts_valid ? (
          <BlockerLine
            icon={<XCircle size={12} />}
            tone="coral"
            text="One or more source URL hosts are not on the pinned allow-list."
          />
        ) : null}
        {validation.is_empty ? (
          <BlockerLine
            icon={<XCircle size={12} />}
            tone="coral"
            text="Every operator proposes zero ranges. Approve is blocked (would disable firewall exemption)."
          />
        ) : null}
        {needsEmptyConfirm ? (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              background: C.amberDim,
              border: `1px solid ${C.amberFg}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
              <AlertTriangle size={12} color={C.amberFg} />
              <span>
                These operators currently have ranges on the active snapshot but
                propose zero ranges here:{' '}
                <strong style={{ fontFamily: monoFont }}>
                  {snapshot.operators_emptied.join(', ')}
                </strong>
                . Confirm to proceed.
              </span>
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
              }}
            >
              <input
                type="checkbox"
                checked={confirmEmpty}
                onChange={(e) => setConfirmEmpty(e.target.checked)}
              />
              I confirm the operators above should be emptied.
            </label>
          </div>
        ) : null}

        <div
          style={{
            marginTop: 16,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => void onApprove(snapshot, confirmEmpty)}
            disabled={approveBlocked}
            style={{
              background: approveBlocked ? C.surface2 : C.limeFg,
              color: approveBlocked ? C.muted : C.ink,
              border: 'none',
              padding: '6px 14px',
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 2,
              cursor: approveBlocked ? 'default' : 'pointer',
            }}
            title={canApprove ? 'Promote this snapshot to active' : 'Owner only'}
          >
            <CheckCircle2
              size={12}
              style={{ display: 'inline-block', verticalAlign: '-2px', marginRight: 4 }}
            />
            {busyApprove ? 'Approving…' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={() => setRejectOpen((v) => !v)}
            disabled={!canApprove || busyReject}
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              padding: '6px 12px',
              fontFamily: sansFont,
              fontSize: 12,
              borderRadius: 2,
              cursor: canApprove && !busyReject ? 'pointer' : 'default',
            }}
            title={canApprove ? 'Reject this snapshot' : 'Owner only'}
          >
            Reject…
          </button>
        </div>

        {rejectOpen ? (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              background: C.surface2,
            }}
          >
            <label
              style={{
                display: 'block',
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1.2,
                color: C.muted,
                marginBottom: 4,
              }}
            >
              REASON (required)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              maxLength={2000}
              style={{
                width: '100%',
                background: C.surface,
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 12,
                padding: 6,
                borderRadius: 2,
                resize: 'vertical',
              }}
            />
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  const trimmed = rejectReason.trim();
                  if (trimmed.length === 0) return;
                  void onReject(snapshot, trimmed).then(() => {
                    setRejectOpen(false);
                    setRejectReason('');
                  });
                }}
                disabled={rejectReason.trim().length === 0 || busyReject}
                style={{
                  background: C.coralFg,
                  color: C.ink,
                  border: 'none',
                  padding: '6px 12px',
                  fontFamily: sansFont,
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 2,
                  cursor:
                    rejectReason.trim().length === 0 || busyReject ? 'default' : 'pointer',
                }}
              >
                {busyReject ? 'Rejecting…' : 'Confirm reject'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setRejectOpen(false);
                  setRejectReason('');
                }}
                style={{
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  color: C.muted,
                  padding: '6px 12px',
                  fontFamily: sansFont,
                  fontSize: 12,
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function PendingOperatorRow({
  op,
}: {
  op: AdminVerifiedCrawlerPendingSnapshot['operators'][number];
}): JSX.Element {
  const C = useC();
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        style={{ color: C.text, fontFamily: sansFont, fontSize: 12, cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        {op.organization}{' '}
        <span style={{ color: C.muted, fontFamily: monoFont, fontSize: 10 }}>{op.operator}</span>
      </div>
      <div style={{ textAlign: 'right', color: C.text, fontFamily: monoFont, fontSize: 11 }}>
        {op.before_count}
      </div>
      <div
        style={{
          textAlign: 'right',
          color: op.after_count === 0 && op.before_count > 0 ? C.coralFg : C.text,
          fontFamily: monoFont,
          fontSize: 11,
        }}
      >
        {op.after_count}
      </div>
      <div
        style={{
          textAlign: 'right',
          color: op.added_count > 0 ? C.limeFg : C.muted,
          fontFamily: monoFont,
          fontSize: 11,
        }}
      >
        +{op.added_count}
      </div>
      <div
        style={{
          textAlign: 'right',
          color: op.removed_count > 0 ? C.coralFg : C.muted,
          fontFamily: monoFont,
          fontSize: 11,
        }}
      >
        −{op.removed_count}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {op.alarm_reason !== null ? (
          <Pill tone={alarmTone(op.alarm_reason)}>
            {op.alarm_reason}
            {op.is_routine ? ' · routine' : ''}
          </Pill>
        ) : (
          <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>—</span>
        )}
      </div>
      {open ? (
        <div
          style={{
            gridColumn: '1 / -1',
            marginTop: 2,
            padding: 10,
            background: C.surface2,
            borderRadius: 2,
            border: `1px solid ${C.border}`,
            fontFamily: monoFont,
            fontSize: 10,
          }}
        >
          {op.added_sample.length > 0 ? (
            <div style={{ marginBottom: 6 }}>
              <div style={{ color: C.limeFg, letterSpacing: 1.2, marginBottom: 2 }}>
                ADDED (sample, up to 50)
              </div>
              {op.added_sample.map((c) => (
                <div key={`a-${c}`} style={{ color: C.text }}>
                  + {c}
                </div>
              ))}
            </div>
          ) : null}
          {op.removed_sample.length > 0 ? (
            <div>
              <div style={{ color: C.coralFg, letterSpacing: 1.2, marginBottom: 2 }}>
                REMOVED (sample, up to 50)
              </div>
              {op.removed_sample.map((c) => (
                <div key={`r-${c}`} style={{ color: C.text }}>
                  − {c}
                </div>
              ))}
            </div>
          ) : null}
          {op.added_sample.length === 0 && op.removed_sample.length === 0 ? (
            <span style={{ color: C.muted }}>no sample rows</span>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function BlockerLine({
  icon,
  tone,
  text,
}: {
  icon: JSX.Element;
  tone: 'coral' | 'amber';
  text: string;
}): JSX.Element {
  const C = useC();
  const fg = tone === 'coral' ? C.coralFg : C.amberFg;
  const bg = tone === 'coral' ? C.coralDim : C.amberDim;
  return (
    <div
      style={{
        marginTop: 10,
        padding: 8,
        background: bg,
        border: `1px solid ${fg}`,
        borderRadius: 2,
        color: C.text,
        fontFamily: sansFont,
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span style={{ color: fg }}>{icon}</span>
      {text}
    </div>
  );
}

/** Mirror the server-side validation on the UI so Approve is greyed
 *  BEFORE the server refuses — the UI never claims an action is
 *  available that the server will 409. The hash and empty-operators
 *  logic come straight from the GET response; CIDR + host flags are
 *  server-computed and surfaced in the same shape. */
function useValidationBlockers(
  active: AdminVerifiedCrawlerActiveSnapshot,
  snapshot: AdminVerifiedCrawlerPendingSnapshot,
): {
  cidrs_valid: boolean;
  source_hosts_valid: boolean;
  is_empty: boolean;
} {
  return useMemo(() => {
    // "is_empty" is derivable from the per-operator after_count on the
    // pending snapshot: if every operator's after_count is zero, the
    // proposal would wipe the exemption list.
    const is_empty = snapshot.operators.every((o) => o.after_count === 0);
    return {
      cidrs_valid: snapshot.cidrs_valid,
      source_hosts_valid: snapshot.source_hosts_valid,
      is_empty,
    };
    // active is in the dep list for parity with any future per-active
    // computation; today it is only passed down the component tree.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, active.snapshot_hash]);
}

function alarmTone(reason: string): 'amber' | 'coral' | 'neutral' {
  if (reason === 'shrink' || reason === 'shape_invalid' || reason === 'multiple') return 'coral';
  if (reason === 'growth' || reason === 'absolute') return 'amber';
  return 'neutral';
}
